import { CSSProperties, useRef } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { GripHorizontal, ImageOff } from 'lucide-react';
import { clampImageWidth } from './imageSizing.ts';
import { IMAGE_ALIGN_STYLES, ImageAlign } from './imageAlign.ts';

// NodeView for `TemplateImage`: renders the preview `<img>` and a corner
// drag-handle to resize it. During the drag we set the width imperatively on
// the element (live feedback, no transaction spam) and commit the final width
// to the node once on pointer-up. `renderHTML` — not this component — produces
// the persisted wire format, so resizing only touches the `width` attribute.
const TemplateImageNodeView = ({
  node,
  updateAttributes,
  selected,
}: NodeViewProps) => {
  const src = node.attrs.src as string | null;
  const alt = (node.attrs.alt as string | null) ?? '';
  const width = node.attrs.width as number | null;
  const align = node.attrs.align as ImageAlign | null;
  const imgRef = useRef<HTMLImageElement>(null);

  const isWrap = align === 'wrap-left' || align === 'wrap-right';
  // The alignment lives on the wrapper in the editor (the persisted `<img>` gets
  // it via `renderHTML`). Non-wrap alignment needs `fit-content` so the wrapper
  // shrinks to the image and `margin:auto` can place it left/center/right while
  // the resize handle stays on the corner; wrapping just floats the wrapper so
  // the following blocks flow around it.
  const wrapperStyle: CSSProperties | undefined = !align
    ? undefined
    : isWrap
      ? IMAGE_ALIGN_STYLES[align]
      : { ...IMAGE_ALIGN_STYLES[align], width: 'fit-content' };

  const startResize = (event: React.PointerEvent) => {
    // stop the node's own drag handler from kicking in
    event.preventDefault();
    event.stopPropagation();

    const img = imgRef.current;
    if (!img) return;

    const startX = event.clientX;
    const startWidth = img.offsetWidth;
    let nextWidth = startWidth;

    const onMove = (moveEvent: PointerEvent) => {
      nextWidth = clampImageWidth(startWidth + (moveEvent.clientX - startX));
      img.style.width = `${nextWidth}px`;
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      updateAttributes({ width: nextWidth });
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <NodeViewWrapper
      as="span"
      className="rich-text-image-wrapper"
      style={wrapperStyle}
      data-selected={selected ? 'true' : undefined}
    >
      {src && (
        // ProseMirror moves the (draggable) node when a drag starts from an
        // element marked `data-drag-handle`. The `<img>` itself is
        // `draggable={false}` (kills the browser's native image drag), so this
        // grip is the grab point for repositioning the image among the text.
        <span
          className="rich-text-image-drag-handle"
          data-drag-handle
          contentEditable={false}
          title="Przeciągnij, aby przenieść"
        >
          <GripHorizontal size={14} />
        </span>
      )}

      {src ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className="rich-text-image"
          style={{ width: width ? `${width}px` : 'auto' }}
          draggable={false}
        />
      ) : (
        <span className="rich-text-image-missing">
          <ImageOff size={20} />
          <span>Grafika niedostępna</span>
        </span>
      )}

      {selected && src && (
        <span
          className="rich-text-image-handle"
          onPointerDown={startResize}
          contentEditable={false}
        />
      )}
    </NodeViewWrapper>
  );
};

export default TemplateImageNodeView;
